import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Loading from "../components/Loading";
import MessageError from "../components/MessageError";
import api from "../helpers/Api";

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const email = useMemo(
        () => (searchParams.get("email") || "").trim().toLowerCase(),
        [searchParams]
    );

    const token = useMemo(
        () => (searchParams.get("token") || "").trim(),
        [searchParams]
    );

    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState("Verificando enlace...");

    const [error, setError] = useState(false);
    const [errorTitle, setErrorTitle] = useState(null);
    const [errorDescription, setErrorDescription] = useState(null);

    const [isValid, setIsValid] = useState(false);

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const showError = (title, message) => {
        setError(true);
        setErrorTitle(title);
        setErrorDescription(message);
    };

    const validateForm = () => {
        if (!newPassword || newPassword.length < 8) {
            return "La contraseña debe tener al menos 8 caracteres.";
        }
        if (newPassword !== confirmPassword) {
            return "Las contraseñas no coinciden.";
        }
        return null;
    };

    useEffect(() => {
        const controller = new AbortController();

        const run = async () => {
            try {
                setError(false);
                setErrorTitle(null); setErrorDescription(null);
                setLoading(true);
                setLoadingText("Verificando enlace...");

                if (!email || !token) {
                    showError(
                        "Enlace inválido",
                        "El enlace de recuperación está incompleto. Solicita uno nuevo."
                    );
                    setLoading(false);
                    return;
                }

                const res = await api.get(`/Auth/Customer/ResetPassword/Verify`, {
                    params: { email, token },
                    signal: controller.signal,
                });

                if (!res?.data?.ok) {
                    showError(
                        "Enlace inválido o expirado",
                        "Este enlace ya no es válido. Solicita uno nuevo."
                    );
                    setIsValid(false);
                    setLoading(false);
                    return;
                }

                setIsValid(true);
                setLoading(false);
            } catch (err) {
                console.error("Error verificando token:", err);

                showError(
                    "No pudimos verificar tu enlace",
                    err?.response?.data?.message ||
                    "Intenta nuevamente o solicita un enlace nuevo."
                );

                setIsValid(false);
                setLoading(false);
            }
        };

        run();

        return () => controller.abort();
    }, [email, token]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const msg = validateForm();
        if (msg) {
            showError("Revisa tu contraseña", msg);
            return;
        }

        const controller = new AbortController();

        try {
            setError(false);
            setErrorTitle(null); setErrorDescription(null);
            setLoading(true);
            setLoadingText("Actualizando tu contraseña...");

            const res = await api.post(
                `/Auth/Customer/ResetPassword`,
                { email, token, newPassword },
                { signal: controller.signal }
            );

            if (!res?.data?.ok) {
                showError(
                    "No se pudo actualizar",
                    res?.data?.message || "No pudimos actualizar tu contraseña. Intenta nuevamente."
                );
                setLoading(false);
                return;
            }

            setLoadingText("Listo. Redirigiendo...");
            setTimeout(() => navigate(`/`), 800);
        } catch (err) {
            if (err?.name === "CanceledError" || err?.name === "AbortError") return;

            console.error("Error reseteando password:", err);

            showError(
                "No se pudo actualizar",
                err?.response?.data?.message ||
                "Ocurrió un error al actualizar tu contraseña. Intenta nuevamente."
            );

            setLoading(false);
        }
    };

    if (loading) return <Loading text={loadingText} />;

    if (error || !isValid) return (
        <MessageError
            title={errorTitle}
            description={errorDescription}
            action={
                <button
                    onClick={() => navigate("/")}
                    style={{ padding: 10, cursor: "pointer" }}
                    className="buy-btn event-one__btn thm-btn"
                >
                    Volver al inicio
                </button>
            }
        />
    );

    return (
        <div className="container" style={{ maxWidth: 520, margin: "40px auto" }}>
            <div className="row">
                <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-200">
                    <img src="/identity/MONOLOGO.png" className="h-6 sm:h-9" alt="Monoticket" />

                    <h2 className="text-2xl font-bold text-black mb-2 font-display">
                        Restablecer contraseña
                    </h2>

                    <p className="text-sm text-gray-600 mb-6">
                        Cuenta: <span className="font-semibold text-black">{email}</span>
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">

                        <div>
                            <label htmlFor="new-password" className="block text-sm font-medium text-black mb-2">
                                Nueva contraseña
                            </label>
                            <input
                                id="new-password"
                                type="password"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Mínimo 8 caracteres"
                                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition"
                            />
                        </div>

                        <div>
                            <label htmlFor="confirm-password" className="block text-sm font-medium text-black mb-2">
                                Confirmar contraseña
                            </label>
                            <input
                                id="confirm-password"
                                type="password"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repite la nueva contraseña"
                                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition"
                            />
                        </div>

                        <button
                            type="submit"
                            className="w-full text-white py-3 rounded-xl font-semibold bg-brand hover:bg-brand-hover text-white w-full disabled:opacity-60 transition"
                        >
                            Cambiar contraseña
                        </button>

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => navigate(`/`)}
                                className="text-sm text-black underline hover:text-gray-700 transition"
                            >
                                Volver al inicio
                            </button>
                        </div>

                    </form>
                </div>

                <div className="mt-4 text-center text-xs text-gray-500">
                    Por seguridad, no podrás reutilizar tu contraseña actual ni la anterior.
                </div>
            </div>
        </div>
    );
}